// src/templates/ImportExportManualWithApollo.js
import React, { useEffect } from "react";
import { ApolloProvider, useQuery } from "@apollo/client";
import { useParams, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import { CircularProgress, Backdrop, Paper, Button } from "@mui/material";

import { importClient } from "../apollo/importClient"; // ← existing client
import LIVE_MANUAL_EXPORT_QUERY from "../craft/dynamicQueries/LiveManualExportQuery";
import { resolveGlobals, resolveManual } from "../craft/dataResolvers";
import PageContextProvider from "../PageContext";
import SimplifiedManualImportExportPage from "../pageTemplates/SimplifiedManualImportExportPage";
import { formatDate } from "../utils";
import ImportHeader from "../components/importexport/ImportHeader";

function ManualLoader({ manualId, siteId }) {
  /* ------------------------------------------------------------
     Run the same live-preview query the preview page uses
  ------------------------------------------------------------ */
  const { data, loading, error } = useQuery(LIVE_MANUAL_EXPORT_QUERY, {
    variables: { id: Number(manualId), siteId: Number(siteId) },
    client: importClient,
  });

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={64} />
      </Box>
    );
  }

  const handleError = (errorObj, sourceString = "") => {
    if (!errorObj) return null;
    // Craft spits this when the token is wrong / schema can’t be built
    const fatal = /getNullableType/i.test(errorObj.message);
    if (fatal) {
      localStorage.removeItem("GATSBY_CRAFT_API_EXPORT_TOKEN");
      return (
        <Box>
          <ImportHeader
            title={`Invalid or expired token ${sourceString}`}
            subHeader="Please enter a new API token"
          />
          <Paper sx={{ p: 4, maxWidth: 480, mx: "auto", textAlign: "center" }}>
            <Button
              variant="contained"
              href="/importexport"
              onClick={() => window.location.replace("/importexport")}
            >
              Go back to token screen
            </Button>
          </Paper>
        </Box>
      );
    }
    return null;
  };
  const errorReturn = handleError(error, "live");
  if (errorReturn) return errorReturn;
  if (error) return <p>Error loading manual: {error.message}</p>;

  /* ------------------------------------------------------------
     Transform Craft data → the shape your UI needs
  ------------------------------------------------------------ */
  const manual = resolveManual(data.entry);
  const { uiText, contact } = resolveGlobals(data.globalSets);
  const { productCategory, productFeatures } = manual;

  const initialState = {
    chrome: {
      title: manual.userTitle,
      logo: manual.logo,
      color: manual.color,
      pageLayoutType: manual.pageLayoutType || "letter",
    },
    uiText,
    contact,
    glossary: data.glossary,
    formattedDate: formatDate(new Date()),
    productCategory,
    productFeatures,
    manualId: manual.id,
    isPreview: true, // so conditional blocks aren't hidden
    isProductionView: true,
    isWebView: true,
  };

  /* ------------------------------------------------------------ */
  return (
    <PageContextProvider initialState={initialState}>
      <SimplifiedManualImportExportPage
        data={data}
        loading={loading}
        error={error}
        siteId={siteId}
      />
    </PageContextProvider>
  );
}

/* ------------------------------------------------------------------ */
/*  Top-level wrapper: get params, persist token, provide client      */
/* ------------------------------------------------------------------ */
export default function ImportExportManualWithApollo() {
  const { manualId, siteId } = useParams();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const token = qs.get("token");

  /* Persist ?token=… so importClient’s authLink picks it up */
  useEffect(() => {
    if (token) {
      localStorage.setItem("GATSBY_CRAFT_API_EXPORT_TOKEN", token);
    }
  }, [token]);

  if (!manualId || !siteId)
    return <p>404 Page not found. Please specify a valid manual and site.</p>;

  return (
    <ApolloProvider client={importClient}>
      <ManualLoader manualId={manualId} siteId={siteId} />
    </ApolloProvider>
  );
}
